import { useState } from 'react'
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  bulkSendDeliveries,
  deleteDelivery,
  downloadInvoiceArchive,
  getDeliveries,
  getDeliveryClients,
  getDeliveryEmailPreview,
  getDeliveryEmailLogs,
  getDeliveryHistory,
  getDeliveryStats,
  getInvoiceDeliveryGlobalSettings,
  matchDelivery,
  previewDraftEmail,
  previewSend,
  sendDelivery,
  sendSelected,
  setDeliveryArchived,
  bulkSyncSendGrid,
  updateInvoiceDeliveryGlobalSettings,
  updateInvoiceDeliveryDueDate,
  updateInvoiceDeliveryMetadata,
  updateInvoiceDeliveryOeSplit,
  updateInvoiceDeliveryTemplate,
  uploadInvoiceFiles,
} from './invoice-delivery.api'
import {
  BulkSendDeliveryRequestDto,
  BulkUpdateDeliveryStatusResponseDto,
  DeleteDeliveryResponseDto,
  DownloadInvoiceArchiveRequestDto,
  GetClientsResponseDto,
  GetDeliveriesRequestDto,
  GetDeliveryStatsRequestDto,
  InvoiceDeliveryEmailLogsResponseDto,
  InvoiceDeliveryGlobalSettingsDto,
  InvoiceDeliveryHistoryResponseDto,
  BulkSyncSendGridResponseDto,
  InvoiceDeliveryItemDto,
  InvoiceDeliveryListResponseDto,
  InvoiceEmailPreviewDto,
  PreviewDraftEmailRequestDto,
  InvoiceDeliveryStatsResponseDto,
  InvoiceDeliveryStatus,
  MatchDeliveryRequestDto,
  SendPreviewRequestDto,
  SendPreviewResponseDto,
  SendSelectedRequestDto,
  SetDeliveryArchivedRequestDto,
  UpdateInvoiceDeliveryGlobalSettingsDto,
  UpdateInvoiceDeliveryDueDateRequestDto,
  UpdateInvoiceDeliveryMetadataRequestDto,
  UpdateInvoiceDeliveryOeSplitRequestDto,
  UpdateInvoiceDeliveryTemplateRequestDto,
  UploadInvoiceFilesResponseDto,
} from './dto/invoice-delivery.dto'

export const INVOICE_DELIVERY_QUERY_KEY = 'invoice-delivery'
const QUERY_KEY = INVOICE_DELIVERY_QUERY_KEY
export const STATS_QUERY_KEY = 'invoice-delivery-stats'
const CLIENTS_QUERY_KEY = 'invoice-delivery-clients'
export const HISTORY_QUERY_KEY = 'invoice-delivery-history'
const EMAIL_LOGS_QUERY_KEY = 'invoice-delivery-email-logs'
const EMAIL_PREVIEW_QUERY_KEY = 'invoice-delivery-email-preview'

export const useGetDeliveriesQuery = (
  params: GetDeliveriesRequestDto,
  options?: Omit<
    UseQueryOptions<
      InvoiceDeliveryListResponseDto,
      Error,
      InvoiceDeliveryListResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    InvoiceDeliveryListResponseDto,
    Error,
    InvoiceDeliveryListResponseDto
  >({
    queryKey: [QUERY_KEY, params],
    queryFn: () => getDeliveries(params),
    enabled: !!params.billingPeriodStart,
    ...options,
  })
}

export const useGetDeliveryStatsQuery = (
  params: GetDeliveryStatsRequestDto,
  options?: Omit<
    UseQueryOptions<
      InvoiceDeliveryStatsResponseDto,
      Error,
      InvoiceDeliveryStatsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    InvoiceDeliveryStatsResponseDto,
    Error,
    InvoiceDeliveryStatsResponseDto
  >({
    queryKey: [STATS_QUERY_KEY, params],
    queryFn: () => getDeliveryStats(params),
    enabled: !!params.billingPeriodStart,
    ...options,
  })
}

export const useUploadInvoiceFilesMutation = (
  options?: UseMutationOptions<
    UploadInvoiceFilesResponseDto,
    Error,
    { files: File[]; billingPeriodStart: string }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    UploadInvoiceFilesResponseDto,
    Error,
    { files: File[]; billingPeriodStart: string }
  >({
    ...options,
    mutationFn: ({ files, billingPeriodStart }) =>
      uploadInvoiceFiles(files, billingPeriodStart),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useSendDeliveryMutation = (
  options?: UseMutationOptions<InvoiceDeliveryItemDto, Error, string>
) => {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDeliveryItemDto, Error, string>({
    ...options,
    mutationFn: (id) => sendDelivery(id),
    onSuccess: (data, variables, context) => {
      // Immediately reflect the updated row in the cache so the status badge
      // switches to Sending without waiting for the background refetch.
      // Optimistically update the row so the status badge switches to Sending
      // immediately. We intentionally skip invalidating the deliveries list here
      // because the status transition is async on the backend — an immediate
      // refetch would race the DB and return Draft, overwriting this update.
      // The polling interval picks up the real state once it settles.
      queryClient.setQueriesData<InvoiceDeliveryListResponseDto>(
        { queryKey: [QUERY_KEY] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            deliveries: old.deliveries.map((d) =>
              d.id === variables ? data : d
            ),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useBulkSendDeliveriesMutation = (
  options?: UseMutationOptions<
    BulkUpdateDeliveryStatusResponseDto,
    Error,
    BulkSendDeliveryRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BulkUpdateDeliveryStatusResponseDto,
    Error,
    BulkSendDeliveryRequestDto
  >({
    ...options,
    mutationFn: bulkSendDeliveries,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

/*
 * A mutation rather than a query: the preview is fetched once when the review
 * modal opens, and must not be refetched underneath the operator while they
 * are ticking boxes — a background refresh would reset their selection.
 */
export const useSendPreviewMutation = (
  options?: UseMutationOptions<
    SendPreviewResponseDto,
    Error,
    SendPreviewRequestDto
  >
) =>
  useMutation<SendPreviewResponseDto, Error, SendPreviewRequestDto>({
    ...options,
    mutationFn: previewSend,
  })

export const useSendSelectedMutation = (
  options?: UseMutationOptions<
    BulkUpdateDeliveryStatusResponseDto,
    Error,
    SendSelectedRequestDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    BulkUpdateDeliveryStatusResponseDto,
    Error,
    SendSelectedRequestDto
  >({
    ...options,
    mutationFn: sendSelected,
    onSuccess: (data, variables, context) => {
      /*
       * Dispatch is fire-and-forget on the server: the response is built from
       * the send plans and returns before SendGrid has accepted, so the rows
       * are still on their pre-send status. Invalidating here would refetch
       * ahead of the transition and leave the badge showing Failed or Draft.
       *
       * Instead mark every dispatched row Sending optimistically — the same
       * approach useSendDeliveryMutation takes — and let the list's polling
       * interval replace it with the settled state. Skipped and failed rows
       * are left alone, since nothing was dispatched for them.
       */
      const dispatchedIds = new Set(
        (data?.results ?? [])
          .filter((r) => r.outcome === 'sent' || r.outcome === 'resent')
          .map((r) => r.deliveryId)
      )
      if (dispatchedIds.size > 0) {
        queryClient.setQueriesData<InvoiceDeliveryListResponseDto>(
          { queryKey: [QUERY_KEY] },
          (old) => {
            if (!old) return old
            return {
              ...old,
              deliveries: old.deliveries.map((d) =>
                dispatchedIds.has(d.id)
                  ? { ...d, status: InvoiceDeliveryStatus.Sending }
                  : d
              ),
            }
          }
        )
      }
      // Stats are counts, not rows — a refetch here cannot clobber a status.
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useDeleteDeliveryMutation = (
  options?: UseMutationOptions<DeleteDeliveryResponseDto, Error, string>
) => {
  const queryClient = useQueryClient()
  return useMutation<DeleteDeliveryResponseDto, Error, string>({
    ...options,
    mutationFn: deleteDelivery,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useGetDeliveryClientsQuery = (
  billingPeriodStart?: string,
  options?: Omit<
    UseQueryOptions<GetClientsResponseDto, Error, GetClientsResponseDto>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<GetClientsResponseDto, Error, GetClientsResponseDto>({
    queryKey: [CLIENTS_QUERY_KEY, billingPeriodStart ?? null],
    queryFn: () => getDeliveryClients(billingPeriodStart),
    staleTime: 5 * 60 * 1000,
    ...options,
  })
}

/** Not cached: edits to this or its paired invoice change the email, so each open loads fresh. */
export const useGetDeliveryEmailPreviewQuery = (id: string) =>
  useQuery<InvoiceEmailPreviewDto, Error>({
    queryKey: [EMAIL_PREVIEW_QUERY_KEY, id],
    queryFn: () => getDeliveryEmailPreview(id),
    gcTime: 0,
    retry: false,
  })

export const usePreviewDraftEmailQuery = (body: PreviewDraftEmailRequestDto) =>
  useQuery<InvoiceEmailPreviewDto, Error>({
    queryKey: [EMAIL_PREVIEW_QUERY_KEY, 'draft', body],
    queryFn: () => previewDraftEmail(body),
    retry: false,
  })

export const useMatchDeliveryMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: MatchDeliveryRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: MatchDeliveryRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => matchDelivery(id, body),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useUpdateInvoiceDeliveryMetadataMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryMetadataRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryMetadataRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => updateInvoiceDeliveryMetadata(id, body),
    onSuccess: (data, variables, context) => {
      // Patch row in-place so the drawer reflects the change immediately.
      queryClient.setQueriesData<InvoiceDeliveryListResponseDto>(
        { queryKey: [QUERY_KEY] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            deliveries: old.deliveries.map((d) =>
              d.id === variables.id ? data : d
            ),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      // Stats include `withWarnings`, which a metadata fix can clear.
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useUpdateInvoiceDeliveryDueDateMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryDueDateRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryDueDateRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => updateInvoiceDeliveryDueDate(id, body),
    onSuccess: (data, variables, context) => {
      // Patch row in-place so the drawer and table reflect the change at once.
      queryClient.setQueriesData<InvoiceDeliveryListResponseDto>(
        { queryKey: [QUERY_KEY] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            deliveries: old.deliveries.map((d) =>
              d.id === variables.id ? data : d
            ),
          }
        }
      )
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useUpdateInvoiceDeliveryTemplateMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryTemplateRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryTemplateRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => updateInvoiceDeliveryTemplate(id, body),
    onSuccess: (data, variables, context) => {
      mergeDeliveryIntoLists(queryClient, variables.id, data)
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useUpdateInvoiceDeliveryOeSplitMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryOeSplitRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: UpdateInvoiceDeliveryOeSplitRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => updateInvoiceDeliveryOeSplit(id, body),
    onSuccess: (data, variables, context) => {
      mergeDeliveryIntoLists(queryClient, variables.id, data)
      options?.onSuccess?.(data, variables, context)
    },
  })
}

/**
 * Patches one row in place, then refetches. Merges rather than replaces: the
 * list adds payment fields a single-delivery response lacks.
 */
const mergeDeliveryIntoLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  data: InvoiceDeliveryItemDto
) => {
  queryClient.setQueriesData<InvoiceDeliveryListResponseDto>(
    { queryKey: [QUERY_KEY] },
    (old) => {
      if (!old) return old
      return {
        ...old,
        deliveries: old.deliveries.map((d) =>
          d.id === id ? { ...d, ...data } : d
        ),
      }
    }
  )
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
  // A split save can clear a Total Due warning, and a template change moves the per-template counts.
  queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
  queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
}

export const useSetDeliveryArchivedMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: SetDeliveryArchivedRequestDto }
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryItemDto,
    Error,
    { id: string; body: SetDeliveryArchivedRequestDto }
  >({
    ...options,
    mutationFn: ({ id, body }) => setDeliveryArchived(id, body),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      // Counts are scoped to the archive filter, so archiving changes them.
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

const GLOBAL_SETTINGS_QUERY_KEY = 'invoice-delivery-global-settings'

export const useGetInvoiceDeliveryGlobalSettingsQuery = (
  options?: Omit<
    UseQueryOptions<
      InvoiceDeliveryGlobalSettingsDto,
      Error,
      InvoiceDeliveryGlobalSettingsDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    InvoiceDeliveryGlobalSettingsDto,
    Error,
    InvoiceDeliveryGlobalSettingsDto
  >({
    queryKey: [GLOBAL_SETTINGS_QUERY_KEY],
    queryFn: getInvoiceDeliveryGlobalSettings,
    staleTime: 60 * 1000,
    ...options,
  })
}

export const useUpdateInvoiceDeliveryGlobalSettingsMutation = (
  options?: UseMutationOptions<
    InvoiceDeliveryGlobalSettingsDto,
    Error,
    UpdateInvoiceDeliveryGlobalSettingsDto
  >
) => {
  const queryClient = useQueryClient()
  return useMutation<
    InvoiceDeliveryGlobalSettingsDto,
    Error,
    UpdateInvoiceDeliveryGlobalSettingsDto
  >({
    ...options,
    mutationFn: updateInvoiceDeliveryGlobalSettings,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [GLOBAL_SETTINGS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useGetDeliveryHistoryQuery = (
  id: string | null,
  /**
   * Archive scope, mirroring the deliveries list. Part of the query key: the
   * same delivery yields different history under each scope, so sharing one
   * cache entry would show the wrong set after toggling.
   */
  isArchived?: boolean,
  options?: Omit<
    UseQueryOptions<
      InvoiceDeliveryHistoryResponseDto,
      Error,
      InvoiceDeliveryHistoryResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    InvoiceDeliveryHistoryResponseDto,
    Error,
    InvoiceDeliveryHistoryResponseDto
  >({
    queryKey: [HISTORY_QUERY_KEY, id, isArchived],
    queryFn: () => getDeliveryHistory(id as string, isArchived),
    enabled: !!id,
    ...options,
  })
}

export const useBulkSyncSendGridMutation = (
  options?: UseMutationOptions<BulkSyncSendGridResponseDto, Error, string>
) => {
  const queryClient = useQueryClient()
  return useMutation<BulkSyncSendGridResponseDto, Error, string>({
    ...options,
    mutationFn: (billingPeriodStart) => bulkSyncSendGrid(billingPeriodStart),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [STATS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [HISTORY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [EMAIL_LOGS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

/*
 * No cache invalidation — this is a read that writes a file to disk, not a
 * mutation of server state. It is modelled as a mutation only because it is
 * user-triggered and needs pending/error state rather than being fetched on
 * render.
 *
 * Also surfaces `receivedBytes` so the UI can show that a long archive download
 * is actually progressing. Byte counts only — the streamed response has no
 * Content-Length, so there is no total to compute a percentage against.
 */
export const useDownloadInvoiceArchiveMutation = (
  options?: UseMutationOptions<
    { fileName: string },
    Error,
    DownloadInvoiceArchiveRequestDto
  >
) => {
  const [receivedBytes, setReceivedBytes] = useState(0)
  const mutation = useMutation<
    { fileName: string },
    Error,
    DownloadInvoiceArchiveRequestDto
  >({
    ...options,
    mutationFn: (params) =>
      downloadInvoiceArchive(params, ({ receivedBytes: loaded }) =>
        setReceivedBytes(loaded)
      ),
    onMutate: (variables) => {
      // Reset here rather than on settle, so the final byte count stays visible
      // until the next download starts.
      setReceivedBytes(0)
      return options?.onMutate?.(variables)
    },
  })
  return { ...mutation, receivedBytes }
}

export const useGetDeliveryEmailLogsQuery = (
  id: string | null,
  options?: Omit<
    UseQueryOptions<
      InvoiceDeliveryEmailLogsResponseDto,
      Error,
      InvoiceDeliveryEmailLogsResponseDto
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    InvoiceDeliveryEmailLogsResponseDto,
    Error,
    InvoiceDeliveryEmailLogsResponseDto
  >({
    queryKey: [EMAIL_LOGS_QUERY_KEY, id],
    queryFn: () => getDeliveryEmailLogs(id as string),
    enabled: !!id,
    ...options,
  })
}
